import { useEffect, useState, useRef } from "react";
import { Link, Navigate } from "react-router-dom";
import api, { num, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { toast } from "sonner";
import { Plus, Trash2, Building2, Mail, Phone, Users, Loader2, X, MapPin, Target, Handshake, ChevronsUpDown, Compass, KeyRound, ChevronDown, ChevronRight, FileWarning } from "lucide-react";
import { ITALIAN_CITIES } from "@/lib/cities";

const EMPTY = { name: "", referente: "", email: "", phone: "", regions: [], provinces: [], manages_direzionali: false, is_direzione: false, chiavi_in_mano: false, notes: "", sede_city: null, sede_op_address: "", lat: null, lng: null };

export default function Agencies() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isAgency = user?.role === "agency";
  const canEditCard = isAdmin || user?.role === "back_office";
  const [showList, setShowList] = useState(false);
  const [agencies, setAgencies] = useState([]);
  const mapRef = useRef(null);
  const [mapFocus, setMapFocus] = useState(null);
  const [regions, setRegions] = useState([]);
  const [provincesByRegion, setProvincesByRegion] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [provOpen, setProvOpen] = useState(false);

  // Esclusività territori: regioni/province già assegnate ad ALTRE agenzie non vanno mostrate
  const assignedRegionsOther = new Set(agencies.filter((a) => a.id !== editing?.id).flatMap((a) => a.regions || []));
  const assignedProvincesOther = new Set(agencies.filter((a) => a.id !== editing?.id).flatMap((a) => a.provinces || []));

  const load = async () => {
    setLoading(true);
    const [a, r, pv] = await Promise.all([api.get("/agencies"), api.get("/regions"), api.get("/provinces")]);
    setAgencies(a.data);
    setRegions(r.data);
    setProvincesByRegion(pv.data || {});
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialog(true); };

  const addRegion = (r) => {
    if (!form.regions.includes(r)) setForm((f) => ({ ...f, regions: [...f.regions, r] }));
  };
  const removeRegion = (r) => setForm((f) => ({ ...f, regions: f.regions.filter((x) => x !== r) }));

  const addProvince = (p) => {
    if (!(form.provinces || []).includes(p)) setForm((f) => ({ ...f, provinces: [...(f.provinces || []), p] }));
  };
  const removeProvince = (p) => setForm((f) => ({ ...f, provinces: (f.provinces || []).filter((x) => x !== p) }));

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/agencies/${editing.id}`, form);
      else await api.post("/agencies", form);
      toast.success(editing ? "Agenzia aggiornata" : "Agenzia creata");
      setDialog(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Eliminare "${a.name}"?`)) return;
    await api.delete(`/agencies/${a.id}`);
    toast.success("Agenzia eliminata");
    load();
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const setSede = (cityName) => {
    const c = ITALIAN_CITIES.find((x) => x.name === cityName);
    if (c) setForm((f) => ({ ...f, sede_city: c.name, lat: c.lat, lng: c.lng }));
  };

  if (isAgency && user?.agency_id) return <Navigate to={`/agencies/${user.agency_id}`} replace />;

  return (
    <div className="space-y-6" data-testid="agencies-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Profilo Agenzie</h1>
          <p className="text-sm text-muted-foreground">{num(agencies.length)} agenzie sul territorio italiano.</p>
        </div>
        {canEditCard && (
          <Button onClick={openNew} data-testid="add-agency-btn"><Plus className="mr-2 h-4 w-4" /> Nuova Agenzia</Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <>
        <div className="relative rounded-xl border-2 border-amber-400/70 bg-amber-400/[0.03] p-4 pt-7" data-testid="agenzie-group">
          <span className="absolute -top-3 left-4 flex items-center gap-1.5 rounded-full bg-amber-400 px-3 py-0.5 text-[11px] font-black uppercase tracking-wide text-black shadow-sm">
            <Building2 className="h-3.5 w-3.5" /> Agenzie
          </span>
          <div className="space-y-6">
        <div className="mb-2 flex items-center gap-2">
          <Building2 className="h-4 w-4 text-primary" />
          <span className="font-heading text-sm font-bold tracking-tight">Elenco agenzie</span>
          <Badge variant="secondary" className="text-[11px]">{agencies.length}</Badge>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {agencies.map((a) => (
            <Card key={a.id} className="flex flex-col p-6" data-testid={`agency-card-${a.id}`}>
              <div className="mb-4 flex items-start justify-between">
                <div className="flex h-11 w-11 items-center justify-center rounded-md bg-primary text-primary-foreground">
                  <Building2 className="h-5 w-5" />
                </div>
                {canEditCard && (
                  <Button variant="ghost" size="icon" onClick={() => remove(a)} data-testid={`delete-agency-${a.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                )}
              </div>
              <h3 className="font-heading text-lg font-bold tracking-tight">{a.name}</h3>
              {a.referente && <p className="text-sm text-muted-foreground">{a.referente}</p>}
              <div className="mt-4 space-y-1.5 text-sm">
                {a.email && <div className="flex items-center gap-2 text-muted-foreground"><Mail className="h-3.5 w-3.5" /> {a.email}</div>}
                {a.phone && <div className="flex items-center gap-2 text-muted-foreground"><Phone className="h-3.5 w-3.5" /> {a.phone}</div>}
                <div className="flex items-center gap-2 text-muted-foreground" data-testid={`agency-clienti-${a.id}`}>
                  <Users className="h-3.5 w-3.5" /> {num(a.clienti_count)} clienti
                  {a.partner_count > 0 && <span className="inline-flex items-center gap-1 text-foreground"><Handshake className="h-3.5 w-3.5 text-primary" />di cui {num(a.partner_count)} partner</span>}
                </div>
                <div className="flex items-center gap-2 text-muted-foreground" data-testid={`agency-prospect-${a.id}`}><Target className="h-3.5 w-3.5" /> {num(a.prospect_count)} {a.prospect_scope === "rete" ? "prospect della rete (vede tutto)" : a.prospect_scope === "direzionali" ? "prospect in carico (+ direzionali)" : "prospect assegnati"}</div>
                <div className="flex items-center gap-2 text-muted-foreground" data-testid={`agency-territori-${a.id}`}><MapPin className="h-3.5 w-3.5" /> {num((a.regions || []).length + (a.provinces || []).length)} territori assegnati</div>
                {(a.contratti_scaduti > 0 || a.contratti_in_scadenza > 0) && (
                  <Link to={`/agencies/${a.id}`} className="flex items-center gap-2 hover:underline" data-testid={`agency-contratti-${a.id}`}>
                    <FileWarning className="h-3.5 w-3.5 text-amber-500" />
                    <span>
                      {a.contratti_scaduti > 0 && <span className="font-semibold text-red-500">{num(a.contratti_scaduti)} scaduti</span>}
                      {a.contratti_scaduti > 0 && a.contratti_in_scadenza > 0 && <span className="text-muted-foreground"> · </span>}
                      {a.contratti_in_scadenza > 0 && <span className="font-semibold text-amber-600">{num(a.contratti_in_scadenza)} in scadenza</span>}
                      <span className="text-muted-foreground"> (contratti)</span>
                    </span>
                  </Link>
                )}
                {a.is_direzione && <div className="flex items-center gap-2 text-primary" data-testid={`agency-direzione-${a.id}`}><Compass className="h-3.5 w-3.5" /> Direzione (vede tutto)</div>}
                {a.manages_direzionali && <div className="flex items-center gap-2 text-primary" data-testid={`agency-direzionali-${a.id}`}><Compass className="h-3.5 w-3.5" /> Gestisce i direzionali</div>}
                {a.chiavi_in_mano && <div className="flex items-center gap-2 text-primary" data-testid={`agency-chiavi-${a.id}`}><KeyRound className="h-3.5 w-3.5" /> Canale chiavi in mano</div>}
              </div>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {(a.regions || []).map((r) => <Badge key={r} variant="secondary" className="text-[11px]">{r}</Badge>)}
                {(a.provinces || []).map((p) => <Badge key={p} variant="outline" className="border-primary/40 text-[11px]">{p}</Badge>)}
              </div>
              <Link to={`/agencies/${a.id}`} className="mt-4" data-testid={`portfolio-link-${a.id}`}>
                <Button variant="outline" className="w-full">Dettaglio Agenzia</Button>
              </Link>
            </Card>
          ))}
          {agencies.length === 0 && <p className="text-muted-foreground">Nessuna agenzia registrata.</p>}
        </div>
          </div>
        </div>
        </>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent data-testid="agency-dialog">
          <DialogHeader><DialogTitle>{editing ? "Modifica Agenzia" : "Nuova Agenzia"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label className="text-xs">Nome Agenzia</Label><Input value={form.name} onChange={set("name")} data-testid="af-name" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs">Agente</Label><Input value={form.referente} onChange={set("referente")} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Telefono</Label><Input value={form.phone} onChange={set("phone")} /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input value={form.email} onChange={set("email")} /></div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1 text-xs"><MapPin className="h-3 w-3" /> Sede (città)</Label>
              <Select value={form.sede_city || undefined} onValueChange={setSede}>
                <SelectTrigger data-testid="af-sede"><SelectValue placeholder="Seleziona la città sede" /></SelectTrigger>
                <SelectContent className="max-h-64">{ITALIAN_CITIES.map((c) => <SelectItem key={c.name} value={c.name}>{c.name} ({c.region})</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">Imposta la posizione del pin sulla mappa del territorio.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1 text-xs"><MapPin className="h-3 w-3" /> Indirizzo sede di partenza</Label>
              <Input value={form.sede_op_address || ""} onChange={set("sede_op_address")} placeholder="Es. Via Roma 12" data-testid="af-sede-address" />
              <p className="text-[11px] text-muted-foreground">Punto di partenza e rientro per il calcolo dei km del funnel visite. Geolocalizzato automatico al salvataggio.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Regioni di competenza</Label>
              <Select onValueChange={addRegion}>
                <SelectTrigger data-testid="af-region"><SelectValue placeholder="Aggiungi regione" /></SelectTrigger>
                <SelectContent>{regions.filter((r) => !form.regions.includes(r) && !assignedRegionsOther.has(r)).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
              <div className="flex flex-wrap gap-1.5 pt-1">
                {form.regions.map((r) => (
                  <Badge key={r} variant="secondary" className="gap-1">
                    {r}<button onClick={() => removeRegion(r)}><X className="h-3 w-3" /></button>
                  </Badge>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Province di competenza</Label>
              <Popover open={provOpen} onOpenChange={setProvOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full justify-between font-normal text-muted-foreground" data-testid="af-province">
                    Aggiungi provincia <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Cerca provincia…" data-testid="af-province-search" />
                    <CommandList className="max-h-72">
                      <CommandEmpty>Nessuna provincia disponibile.</CommandEmpty>
                      {Object.keys(provincesByRegion).sort().map((reg) => {
                        const opts = (provincesByRegion[reg] || []).filter((p) => !(form.provinces || []).includes(p) && !assignedProvincesOther.has(p));
                        if (opts.length === 0) return null;
                        return (
                          <CommandGroup key={reg} heading={reg}>
                            {opts.map((p) => (
                              <CommandItem key={p} value={`${p} ${reg}`} onSelect={() => { addProvince(p); setProvOpen(false); }} data-testid={`af-province-opt-${p}`}>{p}</CommandItem>
                            ))}
                          </CommandGroup>
                        );
                      })}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-[11px] text-muted-foreground">La provincia assegnata ha priorità sulla regione (più specifica). I territori già assegnati ad altre agenzie non compaiono in elenco.</p>
              <div className="flex flex-wrap gap-1.5 pt-1">
                {(form.provinces || []).map((p) => (
                  <Badge key={p} variant="outline" className="gap-1 border-primary/40" data-testid={`af-province-badge-${p}`}>
                    {p}<button onClick={() => removeProvince(p)}><X className="h-3 w-3" /></button>
                  </Badge>
                ))}
              </div>
            </div>
            <label className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/5 p-3 cursor-pointer" data-testid="af-direzione-wrap">
              <Checkbox checked={!!form.is_direzione} onCheckedChange={(v) => setForm((f) => ({ ...f, is_direzione: !!v }))} data-testid="af-direzione" className="mt-0.5" />
              <span className="text-xs leading-relaxed">
                <span className="font-semibold flex items-center gap-1"><Compass className="h-3.5 w-3.5 text-primary" />Figura di Direzione (Responsabile / Head of Partners)</span>
                <span className="text-muted-foreground">Se attivo: vede <b>tutti</b> i contatti e le agenzie e <b>non</b> diventa titolare dei contatti (quelli col suo agente vanno a territorio/direzionale).</span>
                <span className="mt-1 block text-[11px] italic text-primary/80">Nota: usalo per Responsabile e Head of Partners — supervisionano tutta la rete ma non "possiedono" un portafoglio.</span>
              </span>
            </label>
            <label className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/5 p-3 cursor-pointer" data-testid="af-direzionali-wrap">
              <Checkbox checked={!!form.manages_direzionali} onCheckedChange={(v) => setForm((f) => ({ ...f, manages_direzionali: !!v }))} data-testid="af-direzionali" className="mt-0.5" />
              <span className="text-xs">
                <span className="font-semibold flex items-center gap-1"><Compass className="h-3.5 w-3.5 text-primary" />Gestisce clienti e prospect direzionali</span>
                <span className="text-muted-foreground">Se attivo, gli agenti di questa agenzia vedono anche i clienti/prospect senza agenzia di riferimento (direzionali), oltre a quelli dei territori assegnati.</span>
                <span className="mt-1 block text-[11px] italic text-primary/80">Nota: NON vede tutto — solo i contatti dei propri territori + quelli senza agenzia (direzionali). Per la visibilità totale usa "Direzione".</span>
              </span>
            </label>
            <label className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/5 p-3 cursor-pointer" data-testid="af-chiavi-wrap">
              <Checkbox checked={!!form.chiavi_in_mano} onCheckedChange={(v) => setForm((f) => ({ ...f, chiavi_in_mano: !!v }))} data-testid="af-chiavi" className="mt-0.5" />
              <span className="text-xs">
                <span className="font-semibold flex items-center gap-1"><KeyRound className="h-3.5 w-3.5 text-primary" />Evidenzia nel canale chiavi in mano</span>
                <span className="text-muted-foreground">Se attivo, l'agenzia diventa visibile su <b>configuratore.torri.solar</b> (esposta tramite l'API pubblica /public/agencies).</span>
              </span>
            </label>
            <div className="space-y-1.5"><Label className="text-xs">Note</Label><Textarea value={form.notes} onChange={set("notes")} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving || !form.name} data-testid="save-agency-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
